import { HOME_MESSENGER_MENU_ITEMS, PRODUCT_NAME } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Icon, cn, initialsOf } from "@sagot/ui";
import { PreviewAvatar } from "./widget-panel-preview";
import { resolvedTheme } from "./widget-preview";
import type { WidgetForm } from "./widget-model";

const K = "deployKnowledge.widgetStudio.home.preview";
const FILL = "bg-[var(--wg-primary)] text-[var(--wg-on-primary)]";
/** Facebook replaces this token with the visitor's first name. */
const FIRST_NAME_TOKEN = "{{user_first_name}}";

interface FrameProps {
  form: WidgetForm;
  agentName: string;
}

/** The widget opening on Home (visitor side of mockups/widget/workflow-states.html, frame 1), themed by `--wg-*`. */
export function WidgetHomeFrame({ form, agentName }: FrameProps) {
  const { t } = useTranslation();
  const { home } = form;
  const theme = resolvedTheme(form.appearance.theme);
  return (
    <div
      data-theme={theme}
      role="group"
      aria-label={t(`${K}.widgetFrame`)}
      data-testid="home-preview-widget"
      className="mx-auto flex w-full max-w-[380px] flex-col overflow-hidden rounded-[var(--wg-radius)] border border-line bg-surface text-fg shadow-widget"
    >
      <div className={cn("px-5 pb-6 pt-5", FILL)}>
        <div className="flex items-center justify-between">
          <PreviewAvatar form={form} agentName={agentName} />
          <Icon name="x" className="size-5" />
        </div>
        <p className="mt-4 text-xl font-extrabold" data-testid="home-preview-greeting">
          {home.greeting || t(`${K}.defaultGreeting`)}
        </p>
        <p className="text-sm" data-testid="home-preview-subtitle">
          {home.subtitle || t(`${K}.defaultSubtitle`)}
        </p>
      </div>
      {home.options.length > 0 ? (
        <ul aria-label={t(`${K}.optionsLabel`)} className="-mt-3 space-y-2 px-4">
          {home.options.map((option) => (
            <li key={option.id} className="flex min-h-11 items-center gap-3 rounded-lg border border-line bg-surface px-3 py-3 shadow-xs">
              <span aria-hidden="true" className="inline-flex size-8 shrink-0 items-center justify-center rounded-md border border-[var(--wg-primary)] text-fg">
                <Icon name={option.icon} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold">{option.label}</span>
                {option.description ? <span className="block truncate text-xs text-fg-muted">{option.description}</span> : null}
              </span>
              <Icon name={option.action === "link" ? "external-link" : "chevron-right"} className="text-fg-subtle" />
            </li>
          ))}
        </ul>
      ) : (
        <p className="px-4 pt-6 text-center text-xs text-fg-subtle">{t(`${K}.noOptions`)}</p>
      )}
      <div className="m-4 rounded-lg border border-line p-4">
        <p className="text-sm font-bold">{t(`${K}.questionTitle`)}</p>
        <p className="text-xs text-fg-muted">{t(`${K}.questionBody`)}</p>
        <div className={cn("mt-3 flex min-h-11 items-center justify-center rounded-[var(--wg-radius)] text-sm font-bold", FILL)}>{t(`${K}.ask`)}</div>
      </div>
      {form.appearance.show_branding ? (
        <p className="pb-3 text-center text-[11px] text-fg-subtle">
          {t(`${K}.poweredBy`, { product: PRODUCT_NAME })} · {t(`${K}.privacy`)}
        </p>
      ) : null}
    </div>
  );
}

/** Messenger before the first message: greeting, Get Started and the persistent menu (first options). */
export function MessengerHomeFrame({ form, pageName }: { form: WidgetForm; pageName: string }) {
  const { t } = useTranslation();
  const { messenger, options } = form.home;
  const greeting = messenger.greeting.replaceAll(FIRST_NAME_TOKEN, t(`${K}.sampleName`));
  const menu = messenger.persistent_menu ? options.slice(0, HOME_MESSENGER_MENU_ITEMS) : [];
  const empty = !greeting && !messenger.get_started && menu.length === 0;
  return (
    <div role="group" aria-label={t(`${K}.messengerFrame`)} data-testid="home-preview-messenger" className="mx-auto max-w-[340px] overflow-hidden rounded-xl border border-line bg-surface">
      <div className="flex items-center gap-3 border-b border-line px-4 py-3">
        <span aria-hidden="true" className="inline-flex size-9 items-center justify-center rounded-pill bg-signal-500 text-xs font-extrabold text-ink-950">
          {initialsOf(pageName)}
        </span>
        <div>
          <p className="text-sm font-bold">{pageName}</p>
          <p className="text-xs text-fg-subtle">{t(`${K}.typicalReply`)}</p>
        </div>
      </div>
      <div className="space-y-3 p-4 text-sm">
        {greeting ? <p className="rounded-lg bg-sunken p-3 text-center text-xs text-fg-muted">{greeting}</p> : null}
        {messenger.get_started ? <div className="flex min-h-11 items-center justify-center rounded-lg bg-primary text-sm font-bold text-on-primary">{t(`${K}.getStarted`)}</div> : null}
        {menu.length > 0 ? (
          <div className="rounded-lg border border-line">
            <p className="border-b border-line px-3 py-2 text-xs font-bold text-fg-subtle">☰ {t(`${K}.menu`)}</p>
            <ul>
              {menu.map((option) => (
                <li key={option.id} className="border-b border-line px-3 py-2 last:border-b-0">
                  {option.label}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {empty ? <p className="text-center text-xs text-fg-subtle">{t(`${K}.messengerEmpty`)}</p> : null}
      </div>
    </div>
  );
}
